import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Home } from './modules/pages/Home';
import { Dashboard } from './modules/pages/Dashboard';
import { Login } from './modules/pages/Login';
import { Wizard } from './modules/pages/wizard/Wizard';
import { BehaviourTracker } from './infrastructure/shared/analytics/BehaviourTracker';
import { GuestTemplateTracker } from './infrastructure/shared/analytics/GuestTemplateTracker';
import { PublicInvitation } from './modules/pages/PublicInvitation';
import { DraftPreview } from './modules/pages/DraftPreview';
import { Features } from './modules/pages/Features';
import { Company } from './modules/pages/Company';
import { Calendar } from './modules/pages/Calendar';
import { Support } from './modules/pages/Support';
import { SubscriptionPage } from './modules/pages/SubscriptionPage';
import { PartnerProfile } from './modules/pages/PartnerProfile';
import { Settings } from './modules/pages/settings/Settings';
import { McpAuthorizePage } from './modules/pages/McpAuthorizePage';
import { FeedbackPage } from './modules/pages/feedback/FeedbackPage';
import { FeedbackDetailPage } from './modules/pages/feedback/FeedbackDetailPage';
import { HelpCenterPage } from './modules/pages/help/HelpCenterPage';
import { HelpArticlePage } from './modules/pages/help/HelpArticlePage';
import { TicketsPage } from './modules/pages/help/TicketsPage';
import { NewTicketPage } from './modules/pages/help/NewTicketPage';
import { TicketDetailPage } from './modules/pages/help/TicketDetailPage';
import { InvitationsPage } from './modules/invitations/InvitationsPage';
import { NotificationsInboxPage } from './modules/pages/notifications/NotificationsInboxPage';
import { Analytics } from './modules/pages/Analytics';
import { DashboardLayout, SiteLayout } from './modules/components/Layout';
import { Header } from './modules/components/Header';
import { SidebarProvider } from './modules/components/sidebar/SidebarContext';
import { ProtectedRoute } from './modules/components/ProtectedRoute';
import { GuestManagementPage } from './modules/guests/GuestManagementPage';
import { RsvpDashboardPage } from './modules/rsvp';
import { BlessingsManagementPage } from './modules/guestbook/BlessingsManagementPage';
import { DigitalPassPage } from './modules/pass/DigitalPassPage';
import { CheckinPage } from './modules/checkin/CheckinPage';
import { GiftExperiencePage } from './modules/gift/GiftExperiencePage';
import { CapsuleGiftDashboardPage } from './modules/gift/CapsuleGiftDashboardPage';
import { AiInvitationsPage } from './modules/ai-invitations/AiInvitationsPage';
import CircularGalleryDemo from '../components/ui/demo';
import { WeddingPlotPlaneDemo } from './modules/pages/demo/WeddingPlotPlaneDemo';
import PlotPlaneTemplateDemo from './modules/pages/demos/PlotPlaneTemplateDemo';
import { LakePalaceDestinationDemo } from './modules/pages/demos/LakePalaceDestinationDemo';
import { IndoGhibliWeddingDemo, PichwaiHeritageWeddingDemo, Storybook3dWeddingDemo } from './modules/pages/demos/ArtisticWeddingDemos';
import { AnalyticsTracker } from './infrastructure/shared/analytics/AnalyticsTracker';
import { ExperiencePreview } from './modules/pages/ExperiencePreview';
import { TemplateDirectPreviewPage } from './modules/pages/TemplateDirectPreviewPage';
import { VirtualWeddingMVP } from './modules/pages/VirtualWeddingMVP';

import { PolicyPage } from './modules/pages/PolicyPage';
import { CatalogPage } from './modules/pages/catalog/CatalogPage';
import { ProductDetailPage } from './modules/pages/catalog/ProductDetailPage';
import { CheckoutPage } from './modules/pages/checkout/CheckoutPage';
import { CartPage } from './modules/pages/cart/CartPage';

function App() {
  return (
    <BrowserRouter>
      {/* Fires GA4 page_view on every SPA route change */}
      <AnalyticsTracker />
      {/* Click / scroll-depth / section-dwell tracking on public marketing pages */}
      <BehaviourTracker />
      {/* page_view + share/directions/music/scroll on guest invitation templates (PII-free) */}
      <GuestTemplateTracker />
      <Routes>
        {/* Public site routes */}
        <Route element={<SidebarProvider><Header /><SiteLayout /></SidebarProvider>}>
          <Route index element={<Home />} />
          <Route path="/features" element={<Features />} />
          {/* The catalog IS the designs page — Designs.tsx listed every raw
              template (unpriced, and one broken preview blanked the whole page).
              Same redirect pattern as /pricing below. */}
          <Route path="/designs" element={<Navigate to="/catalog" replace />} />
          {/* The catalog IS the real pricing page — Pricing.tsx's hardcoded
              subscription tiers predated the Product Catalog/Pricing Engine
              and were never connected to it. Redirect rather than duplicate. */}
          <Route path="/pricing" element={<Navigate to="/catalog" replace />} />
          <Route path="/company" element={<Company />} />
          <Route path="/templates" element={<Navigate to="/catalog" replace />} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/catalog/:productId" element={<ProductDetailPage />} />

          {/* Policy Routes */}
          <Route path="/terms" element={<PolicyPage defaultSlug="terms" />} />
          <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
          <Route path="/privacy-policy" element={<PolicyPage defaultSlug="privacy-policy" />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/acceptable-use-policy" element={<PolicyPage defaultSlug="acceptable-use-policy" />} />
          <Route path="/cancellation-refund-policy" element={<PolicyPage defaultSlug="cancellation-refund-policy" />} />
          <Route path="/refund-policy" element={<Navigate to="/cancellation-refund-policy" replace />} />
          <Route path="/shipping-delivery-policy" element={<PolicyPage defaultSlug="shipping-delivery-policy" />} />
          <Route path="/shipping-policy" element={<Navigate to="/shipping-delivery-policy" replace />} />
          <Route path="/delivery-policy" element={<Navigate to="/shipping-delivery-policy" replace />} />
          <Route path="/contact-us" element={<PolicyPage defaultSlug="contact-us" />} />
          <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
          <Route path="/cookies-policy" element={<PolicyPage defaultSlug="cookies-policy" />} />
          <Route path="/copyrights-policy" element={<PolicyPage defaultSlug="copyrights-policy" />} />
          <Route path="/content-moderation-policy" element={<PolicyPage defaultSlug="content-moderation-policy" />} />
          <Route path="/disclaimer" element={<PolicyPage defaultSlug="disclaimer" />} />
          <Route path="/security-policy" element={<PolicyPage defaultSlug="security-policy" />} />
          <Route path="/subscription-policy" element={<PolicyPage defaultSlug="subscription-policy" />} />
          <Route path="/support-policy" element={<PolicyPage defaultSlug="support-policy" />} />
          <Route path="/policy/:slug" element={<PolicyPage />} />
        </Route>

        {/* Auth routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Login initialTab="signup" />} />
        <Route path="/i/:slug" element={<PublicInvitation />} />
        <Route path="/draft-preview/:id" element={<DraftPreview />} />
        {/* MCP OAuth consent screen for the InviteCloud AI desktop extension.
            Handles its own auth check (not ProtectedRoute) so the pending
            authorize query params survive a login round-trip. */}
        <Route path="/mcp/authorize" element={<McpAuthorizePage />} />
        {/* Public digital pass — guests open this from their invitation email */}
        <Route path="/pass/:guestCode" element={<DigitalPassPage />} />
        {/* Public Memory Capsule Gift experience */}
        <Route path="/gift/:giftSlug" element={<GiftExperiencePage />} />
        {/* Demo route for 21st Dev Circular Gallery */}
        <Route path="/demo" element={<CircularGalleryDemo />} />
        {/* Public preview of the plot-plane wedding journey */}
        <Route path="/demo/wedding-plot-plane" element={<WeddingPlotPlaneDemo />} />
        <Route
          path="/demo/royal-mandap-palace"
          element={<PlotPlaneTemplateDemo templateId="royal-mandap-palace" />}
        />
        <Route
          path="/demo/marigold-haveli-journey"
          element={<PlotPlaneTemplateDemo templateId="marigold-haveli-journey" />}
        />
        {/* Demo route for the Lake Palace Destination template */}
        <Route path="/demo/lake-palace-destination" element={<LakePalaceDestinationDemo />} />
        <Route path="/demo/indo-ghibli-wedding" element={<IndoGhibliWeddingDemo />} />
        <Route path="/demo/pichwai-heritage-wedding" element={<PichwaiHeritageWeddingDemo />} />
        <Route path="/demo/storybook-3d-wedding" element={<Storybook3dWeddingDemo />} />
        <Route path="/experience-preview" element={<ExperiencePreview />} />
        {/* Direct template preview route for quick testing and Playwright E2E */}
        <Route path="/preview/:templateId" element={<TemplateDirectPreviewPage />} />
        {import.meta.env.DEV && (
          <Route path="/virtual-wedding" element={<VirtualWeddingMVP />} />
        )}

        {/* Protected routes — redirect to /login if unauthenticated */}
        <Route element={<ProtectedRoute />}>
          <Route path="/ai-invitations" element={<AiInvitationsPage />} />
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="ai-invitations" element={<AiInvitationsPage />} />
            <Route path="invitations" element={<InvitationsPage />} />
            <Route
              path="invitations/:invitationId/guests"
              element={<GuestManagementPage />}
            />
            <Route
              path="invitations/:invitationId/rsvps"
              element={<RsvpDashboardPage />}
            />
            <Route
              path="invitations/:invitationId/capsule-gift"
              element={<CapsuleGiftDashboardPage />}
            />
            <Route
              path="invitations/:invitationId/blessings"
              element={<BlessingsManagementPage />}
            />
            <Route path="drafts" element={<Navigate to="/dashboard/invitations" replace />} />
            <Route path="calendar" element={<Calendar />} />
            <Route element={<ProtectedRoute permission="VIEW_ANALYTICS" />}>
              <Route path="analytics" element={<Analytics />} />
            </Route>
            <Route path="partner" element={<PartnerProfile />} />
            <Route path="credits" element={<Navigate to="/dashboard/partner?tab=credits" replace />} />
            <Route path="chat" element={<Support />} />
            <Route path="feedback" element={<FeedbackPage />} />
            <Route path="feedback/:id" element={<FeedbackDetailPage />} />
            <Route path="notifications" element={<NotificationsInboxPage />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="settings" element={<Settings />} />
            <Route path="help" element={<HelpCenterPage />} />
            <Route path="help/articles/:slug" element={<HelpArticlePage />} />
            <Route path="help/tickets" element={<TicketsPage />} />
            <Route path="help/tickets/new" element={<NewTicketPage />} />
            <Route path="help/tickets/:id" element={<TicketDetailPage />} />
          </Route>
          <Route path="/wizard" element={<Wizard />} />
          {/* Pricing Engine / Purchase checkout — authenticated (a Purchase belongs to a user) */}
          <Route path="/checkout/:productId" element={<CheckoutPage />} />
          {/* Cart — authenticated (a Cart belongs to a user), see 09_CART_DOMAIN.md */}
          <Route path="/cart" element={<CartPage />} />
          {/* Gate-staff check-in app — requires the CHECKIN_VERIFY permission */}
          <Route element={<ProtectedRoute permission="CHECKIN_VERIFY" />}>
            <Route path="/checkin" element={<CheckinPage />} />
          </Route>
        </Route>

        <Route path="/share" element={<Navigate to="/" replace />} />
        <Route path="/export" element={<Navigate to="/" replace />} />
        <Route path="/pdf" element={<Navigate to="/" replace />} />
        <Route path="/gif" element={<Navigate to="/" replace />} />
        <Route path="/download" element={<Navigate to="/" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
